import React from 'react';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Checkbox from '@material-ui/core/Checkbox';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import Input from '@material-ui/core/Input';
import Button from '@material-ui/core/Button';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import TextField from '@material-ui/core/TextField';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import SearchIcon from '@material-ui/icons/Search';
import InputAdornment from '@material-ui/core/InputAdornment';
import _ from 'underscore';
import theme from 'utils/theme';
import { MuiThemeProvider } from '@material-ui/core/styles';
import { AutoSizer, List } from 'react-virtualized';
import './MultipleSelect.css';
import { i18n } from 'utils/translate';

function CheckboxMenuItem({ label, value, checked, onChange }) {
    return (
        <FormControlLabel
            label={label}
            className="multiselect__item__label"
            control={
                <Checkbox
                    checked={checked}
                    value={value.toString()}
                    onChange={onChange}
                    color="primary"
                />
            }
        />
    );
}

const LIST_ITEMS = 4;
const ITEM_HEIGHT = 46;
const SEARCH_TIMEOUT = 600;

class MultipleSelect extends React.Component {
    state = {
        selectedOptions: [],
        visibleOptions: this.props.options,
        searchText: '',
        anchorEl: null,
        finalSelectedOptions: [],
    };

    componentDidMount() {
        const { options, defaultSelection } = this.props;
        const selectedOptions = options.filter((elem) =>
            defaultSelection.includes(elem.value)
        );

        this.setState({
            selectedOptions,
            finalSelectedOptions: selectedOptions,
        });
    }

    componentDidUpdate(prevProps) {
        const { options, defaultSelection } = this.props;

        if (defaultSelection !== prevProps.defaultSelection) {
            const selectedOptions = options.filter((elem) =>
                defaultSelection.includes(elem.value)
            );
            this.setState({
                selectedOptions,
                finalSelectedOptions: selectedOptions,
            });
        }

        if (options !== prevProps.options) {
            this.setState({ visibleOptions: options, searchText: '' });
        }
    }

    handleClick = (event) => {
        const { currentTarget } = event;
        this.setState({ anchorEl: currentTarget });
    };

    handleClickAway = () => {
        this.setState({ anchorEl: null });
    };

    handleCheckboxChange = (event) => {
        const { value } = event.target;
        this.setState((prevState) => {
            let { selectedOptions } = prevState;
            const option = this.props.options.find(
                (option) => option.value.toString() === value.toString()
            );
            const isSelectedAlready =
                undefined !==
                selectedOptions.find(
                    (selected) => selected.value === option.value
                );

            if (isSelectedAlready) {
                selectedOptions = selectedOptions.filter(
                    (selected) => selected.value !== option.value
                );
            } else {
                selectedOptions = [...selectedOptions, option];
            }

            return { selectedOptions };
        });
    };

    handleAllChange = (event) => {
        if (event.target.checked) {
            this.setState({ selectedOptions: this.props.options });
        } else {
            this.setState({ selectedOptions: [] });
        }
    };

    handleSearchTyping = (event) => {
        const { searchTimeout } = this.state;
        const searchText = event.target.value;

        if (searchTimeout) {
            clearTimeout(this.searchAutocompleterTimeout);
            delete this.searchAutocompleterTimeout;
        }

        this.setState({
            searchTimeout: setTimeout(
                this.createSearchTimeoutHandler(searchText),
                SEARCH_TIMEOUT
            ),
        });
    };

    createSearchTimeoutHandler = (searchText) => () => {
        const filterSearch = searchText.split(/\s+/);

        let visibleOptions = [...this.props.options];
        if (filterSearch.length > 0) {
            const searchRegex = new RegExp(
                `(?=.*${filterSearch.join(')(?=.*')})`,
                'i'
            );
            visibleOptions = this.props.options.filter((option) =>
                searchRegex.test(option.label)
            );
        }

        this.setState({ visibleOptions });
    };

    handleSearchChange = (event) => {
        const searchText = event.target.value;
        this.setState({ searchText });
    };

    prepareHandleChangeSelectedValues() {
        const { selectedOptions } = this.state;

        if (selectedOptions.length === this.props.options.length) {
            return [];
        }

        return selectedOptions.map((selected) => selected.value).sort();
    }

    handleAddClick = () => {
        if (this.props.handleChange) {
            this.props.handleChange(this.prepareHandleChangeSelectedValues());
        }

        this.setState((prevState) => ({
            anchorEl: null,
            finalSelectedOptions: prevState.selectedOptions,
        }));
    };

    getLabel = () => {
        const { finalSelectedOptions } = this.state;
        let suffix = '';
        if (finalSelectedOptions.length > 0) {
            suffix = ` (${finalSelectedOptions.length})`;
        }

        return this.props.label + suffix;
    };

    isOptionSelected = (option) =>
        this.state.selectedOptions.findIndex(
            (selected) => selected.value === option.value
        ) > -1;

    rowRenderer = ({ key, index, style }) => {
        if (index === 0) {
            return (
                <div key={key} style={style}>
                    <CheckboxMenuItem
                        label={i18n('js_all')}
                        value=""
                        checked={
                            this.props.options.length ===
                            this.state.selectedOptions.length
                        }
                        onChange={this.handleAllChange}
                    />
                </div>
            );
        }

        const option = this.state.visibleOptions[index - 1];
        return (
            <div key={key} style={style}>
                <CheckboxMenuItem
                    label={option.label}
                    value={option.value}
                    checked={this.isOptionSelected(option)}
                    onChange={this.handleCheckboxChange}
                />
            </div>
        );
    };

    render() {
        const { style, disabled, placeholder } = this.props;
        const {
            anchorEl,
            finalSelectedOptions,
            visibleOptions,
            searchText,
            selectedOptions,
        } = this.state;
        const open = Boolean(anchorEl) && !disabled;
        const uniqueId = _.uniqueId('multiselect');
        const popperId = `${uniqueId}Popper`;

        return (
            <div className="multiselect" style={style}>
                <MuiThemeProvider theme={theme}>
                    <FormControl component="fieldset">
                        <TextField
                            disabled={disabled}
                            label={this.getLabel()}
                            value={
                                finalSelectedOptions.length
                                    ? finalSelectedOptions
                                          .map((selected) => selected.label)
                                          .sort()
                                          .join(', ')
                                    : placeholder
                            }
                            className="multiselect__textfield"
                            margin="normal"
                            InputProps={{
                                readOnly: true,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <ArrowDropDown />
                                    </InputAdornment>
                                ),
                            }}
                            inputProps={{
                                className: 'multiselect__textfield__input',
                            }}
                            onClick={this.handleClick}
                            aria-describedby={popperId}
                        />
                        <Popper
                            id={popperId}
                            open={open}
                            anchorEl={anchorEl}
                            className="multiselect__popper"
                        >
                            <ClickAwayListener
                                onClickAway={this.handleClickAway}
                            >
                                <Paper>
                                    <div>
                                        <Input
                                            placeholder="Search"
                                            value={searchText}
                                            onKeyUp={this.handleSearchTyping}
                                            onChange={this.handleSearchChange}
                                            className="multiselect__search-input"
                                            endAdornment={
                                                <InputAdornment position="end">
                                                    <SearchIcon />
                                                </InputAdornment>
                                            }
                                        />
                                    </div>

                                    <AutoSizer
                                        defaultHeight={ITEM_HEIGHT * LIST_ITEMS}
                                        disableHeight
                                    >
                                        {({ width }) => (
                                            <List
                                                height={
                                                    ITEM_HEIGHT * LIST_ITEMS
                                                }
                                                width={width}
                                                rowCount={
                                                    visibleOptions.length + 1
                                                }
                                                rowHeight={ITEM_HEIGHT}
                                                rowRenderer={this.rowRenderer}
                                                selectedOptions={
                                                    selectedOptions
                                                }
                                            />
                                        )}
                                    </AutoSizer>

                                    <span className="multiselect__buttons-container">
                                        <Button
                                            color="primary"
                                            onClick={this.handleAddClick}
                                        >
                                            {i18n('js_add').toUpperCase()}
                                        </Button>
                                    </span>
                                </Paper>
                            </ClickAwayListener>
                        </Popper>
                    </FormControl>
                </MuiThemeProvider>
            </div>
        );
    }
}

MultipleSelect.defaultProps = {
    defaultSelection: [],
    placeholder: i18n('js_all'),
};

export default MultipleSelect;
